'use client'

import { Clock16, LocationPin16 } from '@vinted/monochrome-icons'
import { Bubble, Icon, Rating, Spacer, Text } from '@vinted/web-ui'
import classNames from 'classnames'

import { useEffect } from 'react'

import { UserPhoto } from '@marketplace-web/messaging/conversation-user-feature'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { useDate } from '@marketplace-web/shared/ui-helpers'
import { calculateRating } from '@marketplace-web/domain/user-rating-util'
import { useAbTest, useTrackAbTestCallback } from '@marketplace-web/feature-flags/ab-tests-data'

const IntroMessage = () => {
  const translate = useTranslate()
  const trackAbTest = useTrackAbTestCallback()
  const introMessageTranslate = useTranslate('conversation.intro_message')
  const { formatRelativeTime } = useDate()
  const { conversation } = useConversationContext()

  const removeLastSeenFromConversationAbTest = useAbTest('remove_last_seen_from_conversation')
  const removeReviewsFromConversationAbTest = useAbTest('remove_reviews_from_conversation')
  const removeLocationFromConversationAbTest = useAbTest('remove_location_from_conversation')

  const isRemoveReviewsFromConversationAbTestEnabled =
    removeReviewsFromConversationAbTest?.variant === 'on'
  const isRemoveLastSeenFromConversationAbTestEnabled =
    removeLastSeenFromConversationAbTest?.variant === 'on'
  const isRemoveLocationFromConversationAbTestEnabled =
    removeLocationFromConversationAbTest?.variant === 'on'

  const oppositeUser = conversation?.oppositeUser

  useEffect(() => {
    if (!conversation?.item || conversation?.oppositeUser?.isSystem) return

    trackAbTest(removeLastSeenFromConversationAbTest)
    trackAbTest(removeReviewsFromConversationAbTest)
    trackAbTest(removeLocationFromConversationAbTest)
  }, [
    conversation,
    removeLastSeenFromConversationAbTest,
    removeLocationFromConversationAbTest,
    removeReviewsFromConversationAbTest,
    trackAbTest,
  ])

  const getLocation = () => {
    if (!oppositeUser) return null

    const { locationDescription } = oppositeUser

    return locationDescription
  }

  const location = getLocation()

  const renderUserPhoto = () => {
    if (!oppositeUser) return null

    const { photo, profileUrl, isSystem } = oppositeUser

    return (
      <div className="u-ui-margin-right-regular">
        <UserPhoto
          photo={photo}
          profileUrl={profileUrl}
          isSystem={isSystem}
          alt={introMessageTranslate('a11y.user_profile_picture', {
            username: oppositeUser.login,
          })}
        />
      </div>
    )
  }

  const renderGreeting = () => {
    if (!oppositeUser) return null

    return (
      <Text
        as="h2"
        text={introMessageTranslate('conversation_greeting', { username: oppositeUser.login })}
        type={Text.Type.Title}
      />
    )
  }

  const renderInfoBlock = (content: JSX.Element | null) => {
    if (!content) return null

    return (
      <>
        <Spacer size={Spacer.Size.Small} />
        <div className="u-flexbox u-align-items-center">{content}</div>
      </>
    )
  }

  const renderFeedbackCount = () => {
    if (!oppositeUser) return null

    const count = oppositeUser.reviewCount || 0

    return introMessageTranslate('reviews_count', { count }, { count })
  }

  const renderRating = () => {
    if (isRemoveReviewsFromConversationAbTestEnabled) return null
    if (!oppositeUser) return null

    const { feedbackReputation } = oppositeUser

    const feedbackRating = calculateRating(feedbackReputation)

    return (
      <>
        <Spacer />
        <Rating
          value={feedbackRating}
          text={renderFeedbackCount()}
          emptyStateText={translate('user.feedback.no_reviews')}
          aria={{
            'aria-label':
              (feedbackRating &&
                introMessageTranslate('a11y.rating', { rating: feedbackRating, max_rating: 5 })) ||
              '',
          }}
        />
      </>
    )
  }

  const renderLocation = () => {
    if (isRemoveLocationFromConversationAbTestEnabled) return null

    if (!location) return null

    return (
      <>
        <Icon
          name={LocationPin16}
          color={Icon.Color.GreyscaleLevel3}
          aria={{ 'aria-hidden': true }}
        />
        <Spacer orientation={Spacer.Orientation.Vertical} />
        <Text
          as="span"
          text={location}
          aria={{
            'aria-label': introMessageTranslate('a11y.user_location', {
              location,
            }),
          }}
        />
      </>
    )
  }

  const renderLastSeen = () => {
    if (isRemoveLastSeenFromConversationAbTestEnabled) return null
    if (!oppositeUser?.lastLoginTime) return null

    const logonDate = new Date(oppositeUser.lastLoginTime)

    return (
      <>
        <Icon name={Clock16} color={Icon.Color.GreyscaleLevel3} aria={{ 'aria-hidden': true }} />
        <Spacer orientation={Spacer.Orientation.Vertical} />
        <Text
          as="span"
          text={`${introMessageTranslate('last_seen')} ${formatRelativeTime(logonDate)}`}
          aria={{
            'aria-label': introMessageTranslate('a11y.last_seen', {
              logon_date: formatRelativeTime(logonDate),
            }),
          }}
        />
      </>
    )
  }

  if (!oppositeUser || oppositeUser.isSystem) return null

  const introClass =
    isRemoveLastSeenFromConversationAbTestEnabled &&
    isRemoveReviewsFromConversationAbTestEnabled &&
    isRemoveLocationFromConversationAbTestEnabled
      ? 'conversation-message'
      : classNames('conversation-message', 'conversation-message__intro')
  const shouldHideSpacer =
    isRemoveLastSeenFromConversationAbTestEnabled && isRemoveLocationFromConversationAbTestEnabled

  return (
    <div className="conversation-message-container">
      {renderUserPhoto()}
      <div className={introClass}>
        <Bubble>
          <div
            aria-label={introMessageTranslate('a11y.message_from_user', {
              username: oppositeUser.login,
            })}
          >
            {renderGreeting()}
            {renderRating()}
            {location && !shouldHideSpacer && <Spacer size={Spacer.Size.Medium} />}
            {renderInfoBlock(renderLocation())}
            {renderInfoBlock(renderLastSeen())}
          </div>
        </Bubble>
      </div>
    </div>
  )
}

export default IntroMessage
